import { PlayerName } from "@/components/PlayerCardDialog";
import React from "react";
import axios from "axios";
import { Loader2, ChevronDown, Trophy } from "lucide-react";
import { ARCHETYPES } from "@/components/ArchetypeBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [["tonight", "Tonight"], ["last7", "Last 7 days"], ["season", "Season"]];

const Row = ({ r, best, testId }) => {
  const [open, setOpen] = React.useState(false);
  const m = ARCHETYPES[r.archetype];
  return (
    <div className="border-t border-slate-800/70" data-testid={testId}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        className="grid w-full grid-cols-[1fr_60px_60px_60px_80px_20px] items-center gap-2 px-3 py-2 text-left font-mono text-[11px] transition-colors hover:bg-fuchsia-950/20">
        <span className={`truncate font-semibold uppercase ${m ? "text-fuchsia-200" : "text-slate-500"}`}>
          {m ? `${m.icon} ` : ""}{r.archetype}{best && <Trophy className="ml-1.5 inline h-3 w-3 text-amber-400" />}
        </span>
        <span className="text-right text-slate-400">{r.players}</span>
        <span className="text-right text-slate-200">{r.scorers}</span>
        <span className="text-right text-slate-400">{r.goals}</span>
        <span className="flex items-center justify-end gap-1.5">
          <span className="h-1.5 w-8 overflow-hidden rounded bg-slate-800">
            <span className="block h-full bg-gradient-to-r from-fuchsia-500 to-amber-400" style={{ width: `${Math.min(r.hit_pct * 2, 100)}%` }} />
          </span>
          <span className="w-10 text-right font-bold text-amber-300">{r.hit_pct}%</span>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 bg-slate-950/60 px-3 pb-2.5 pt-1" data-testid={`${testId}-scorers`}>
          {r.scorer_list.length ? r.scorer_list.map((w) => (
            <span key={`${w.team}-${w.jersey}-${w.name}`} className="rounded border border-slate-800 bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] text-slate-300">
              {w.team} #{w.jersey} <PlayerName name={w.name} team={w.team} /> <span className="text-amber-300">{w.goals}G</span>
              {w.nights > 1 && <span className="text-slate-500"> / {w.nights} nights</span>}
            </span>
          )) : <span className="font-mono text-[10px] text-slate-600">No scorers yet</span>}
          <span className="w-full font-mono text-[9px] text-slate-600">{r.goals_per_player} goals per player</span>
        </div>
      )}
    </div>
  );
};

const Note = ({ win, d }) => {
  const t = d.tonight;
  if (win === "tonight") {
    if (!t.tracked) return "No pre-game snapshot for tonight (captured on first load before puck drop).";
    return `${t.tracked} skaters snapshotted pre-game · ${t.games_final}/${t.games_total} games final${t.started ? " · live, dressed players only" : " · games not started"}`;
  }
  const n = d[win].nights;
  return n ? `${n} graded night${n > 1 ? "s" : ""} · dressed players only` : "No graded nights yet — first results appear after tonight's games go final.";
};

export default function ArchetypeHitRates({ apiDate }) {
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState(false);
  const [win, setWin] = React.useState("tonight");
  React.useEffect(() => {
    setD(null); setErr(false);
    axios.get(`${API}/archetype-hit-rates`, { params: apiDate ? { date: apiDate } : {}, timeout: 120000 })
      .then((r) => setD(r.data)).catch(() => setErr(true));
  }, [apiDate]);

  const rows = d?.[win]?.rows || [];
  const bestKey = rows.filter((r) => ARCHETYPES[r.archetype] && r.players >= 3)
    .sort((a, b) => b.hit_pct - a.hit_pct)[0]?.archetype;

  return (
    <section className="mb-6 rounded-xl border border-fuchsia-900/40 bg-slate-900/30" data-testid="archetype-hit-rates">
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 pt-3">
        <div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-fuchsia-200">Archetype Hit Rates</h3>
          <p className="font-mono text-[10px] text-slate-500" data-testid="hit-rates-note">{d ? <Note win={win} d={d} /> : "Pre-game archetype vs actual goal scorers"}</p>
        </div>
        <div className="flex rounded-md border border-slate-800 bg-slate-950 p-0.5">
          {WINDOWS.map(([k, l]) => (
            <button key={k} data-testid={`hit-window-${k}`} onClick={() => setWin(k)}
              className={`rounded px-3 py-1 font-mono text-[11px] font-bold transition-colors ${win === k ? "bg-fuchsia-500/20 text-fuchsia-200" : "text-slate-500 hover:text-slate-300"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <div className="grid grid-cols-[1fr_60px_60px_60px_80px_20px] gap-2 px-3 pb-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-500">
          <span>Archetype</span><span className="text-right">Players</span><span className="text-right">Scorers</span>
          <span className="text-right">Goals</span><span className="text-right">Hit %</span><span />
        </div>
        {err ? <p className="px-3 py-4 font-mono text-[11px] text-rose-400" data-testid="hit-rates-error">Hit rates failed to load</p>
          : !d ? <div className="flex items-center gap-2 px-3 py-4 font-mono text-[11px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-fuchsia-400" /> Loading hit rates…</div>
          : rows.length ? rows.map((r) => <Row key={`${win}-${r.archetype}`} r={r} best={r.archetype === bestKey && r.scorers > 0} testId={`hit-row-${win}-${r.archetype.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />)
          : <p className="px-3 py-4 font-mono text-[11px] text-slate-600" data-testid="hit-rates-empty">No data for this window yet</p>}
      </div>
      <div className="h-2" />
    </section>
  );
}
